import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tip } from '../components/ui/tip';

/**
 * Finanzas → Posición → "repartir a cuentas": asignar a qué cuenta propia
 * corresponde cada movimiento que quedó sin cuenta (transferencias, débito,
 * tarjetas, cheques). El efectivo no aparece: va solo a la cuenta de efectivo.
 */

const fmtARS = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fecha = (s: string) => (s ? new Date(s + 'T12:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');

const BUCKET_DE = (m: string) => {
  const code = String(m || '').toLowerCase();
  if (code === 'efectivo') return 'Efectivo';
  if (['transferencia', 'debito', 'tc3', 'tc6', 'credito', 'tarjeta'].includes(code)) return 'Banco';
  if (code === 'qr') return 'MercadoPago / QR';
  if (code === 'cheque') return 'Cheques';
  return 'Sin asignar';
};
const BUCKETS = ['Todos', 'Banco', 'MercadoPago / QR', 'Cheques', 'Sin asignar'];
const TIPO_LABEL: Record<string, string> = { remito: 'Remito', remito_pago: 'Remito (parte)', cobranza: 'Cobranza', gasto: 'Gasto' };
// Un cobro mixto entra como un renglón por medio de pago ('remito_pago')
const esRemito = (tipo: string) => tipo === 'remito' || tipo === 'remito_pago';

interface Movement {
  id: string;
  tipo: string;
  ref: string;
  fecha: string;
  metodo: string;
  monto: number;
  detalle: string;
  items?: string[];
  vehiculo?: string | null;
}

export default function AssignAccounts() {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Movement[]>([]);
  const [accounts, setAccounts] = useState<{ id: string; name: string; kind?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState(searchParams.get('bucket') || 'Todos');
  const [sel, setSel] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [movRes, accRes] = await Promise.all([
        axios.get('/api/financial-accounts/unassigned'),
        axios.get('/api/financial-accounts'),
      ]);
      setRows(movRes.data.movements || []);
      setAccounts(accRes.data.accounts || []);
    } catch { toast.error('Error al cargar los movimientos'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const visibles = useMemo(
    () => rows.filter(r => bucket === 'Todos' || BUCKET_DE(r.metodo) === bucket),
    [rows, bucket]
  );
  const totalVisible = useMemo(() => visibles.reduce((s, r) => s + Number(r.monto || 0), 0), [visibles]);
  const countByBucket = useMemo(() => {
    const map: Record<string, number> = {};
    rows.forEach(r => { const b = BUCKET_DE(r.metodo); map[b] = (map[b] || 0) + 1; });
    return map;
  }, [rows]);

  const confirmar = async (row: Movement) => {
    const accountId = sel[row.id];
    if (!accountId) { toast.error('Elegí una cuenta'); return; }
    setBusy(row.id);
    try {
      await axios.post('/api/financial-accounts/assign', { tipo: row.tipo, id: row.id, accountId });
      const acc = accounts.find(a => a.id === accountId);
      toast.success(`${row.ref || TIPO_LABEL[row.tipo]} asignado a ${acc?.name || 'la cuenta'}`);
      setRows(prev => prev.filter(r => r.id !== row.id));
    } catch (err: any) { toast.error(err.response?.data?.error || 'Error al asignar'); }
    finally { setBusy(''); }
  };

  // Tooltip del remito: qué se hizo + cliente + vehículo
  const remitoTip = (r: Movement) => (
    <div className="max-w-[280px] space-y-1 text-left">
      {r.detalle ? <p className="font-semibold">{r.detalle}</p> : null}
      {r.vehiculo ? <p className="text-xs opacity-80">🚗 {r.vehiculo}</p> : null}
      {(r.items || []).length > 0 ? (
        <ul className="text-xs list-disc pl-4 space-y-0.5">
          {(r.items || []).slice(0, 8).map((it, i) => <li key={i}>{it}</li>)}
          {(r.items || []).length > 8 ? <li>… y {(r.items || []).length - 8} más</li> : null}
        </ul>
      ) : <p className="text-xs opacity-70">Sin detalle de items</p>}
    </div>
  );

  return (
    <div className="space-y-4 p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <Link to="/finanzas/posicion" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1">
            <ArrowLeftIcon className="h-3.5 w-3.5" /> Volver a Posición
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">Asignar movimientos a cuentas</h1>
          <p className="text-sm text-muted-foreground">
            Cobros y gastos que todavía no dicen por qué cuenta pasaron. Elegí la cuenta y confirmá con ✓ — el saldo se mueve ahí.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Pendiente {bucket !== 'Todos' ? `· ${bucket}` : ''}</p>
          <p className="text-xl font-bold">{fmtARS(totalVisible)}</p>
          <p className="text-xs text-muted-foreground">{visibles.length} movimiento{visibles.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      {/* Filtro por bucket */}
      <div className="flex gap-1.5 flex-wrap">
        {BUCKETS.map(b => (
          <button
            key={b}
            type="button"
            onClick={() => setBucket(b)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              bucket === b ? 'bg-foreground text-background border-foreground' : 'bg-background text-muted-foreground border-border hover:text-foreground'
            }`}
          >
            {b}{b !== 'Todos' && countByBucket[b] ? ` (${countByBucket[b]})` : ''}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
          ) : visibles.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">No queda nada por asignar acá. 🎉</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b">
                  <TableHead className="h-9 px-3 text-xs">Fecha</TableHead>
                  <TableHead className="h-9 px-3 text-xs">Tipo</TableHead>
                  <TableHead className="h-9 px-3 text-xs">Remito</TableHead>
                  <TableHead className="h-9 px-3 text-xs">Cliente</TableHead>
                  <TableHead className="h-9 px-3 text-xs">Medio</TableHead>
                  <TableHead className="h-9 px-3 text-xs text-right">Monto</TableHead>
                  <TableHead className="h-9 px-3 text-xs w-[200px]">Cuenta</TableHead>
                  <TableHead className="h-9 w-14"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibles.map(r => (
                  <TableRow key={r.id} className="border-b hover:bg-muted/30">
                    <TableCell className="px-3 py-2 text-sm whitespace-nowrap">{fecha(r.fecha)}</TableCell>
                    <TableCell className="px-3 py-2"><Badge variant="secondary" className="text-xs">{TIPO_LABEL[r.tipo] || r.tipo}</Badge></TableCell>
                    <TableCell className="px-3 py-2 text-sm">
                      {esRemito(r.tipo) && r.ref ? (
                        <Tip label={remitoTip(r)} side="right">
                          <span className="font-mono text-xs underline decoration-dotted underline-offset-4 cursor-help">{r.ref}</span>
                        </Tip>
                      ) : (
                        <span className="text-xs text-muted-foreground max-w-[220px] truncate inline-block align-bottom">{r.detalle || '—'}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-2 text-sm max-w-[180px] truncate">{esRemito(r.tipo) ? (r.detalle || '—') : '—'}</TableCell>
                    <TableCell className="px-3 py-2 text-sm text-muted-foreground whitespace-nowrap">{r.metodo || '—'}</TableCell>
                    <TableCell className={`px-3 py-2 text-sm text-right font-semibold whitespace-nowrap ${Number(r.monto) < 0 ? 'text-red-500' : ''}`}>{fmtARS(Number(r.monto))}</TableCell>
                    <TableCell className="px-3 py-2">
                      <Select value={sel[r.id] || ''} onValueChange={v => setSel(prev => ({ ...prev, [r.id]: v }))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Elegir cuenta..." /></SelectTrigger>
                        <SelectContent>
                          {/* Acá solo hay movimientos NO efectivo → la caja de efectivo no es opción */}
                          {accounts.filter(a => a.kind !== 'efectivo').map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="px-2 py-2 text-right">
                      <Tip label="Confirmar asignación">
                        <Button size="icon" className="size-8" disabled={!sel[r.id] || busy === r.id} onClick={() => confirmar(r)}>
                          <CheckIcon className="h-4 w-4" />
                        </Button>
                      </Tip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        El efectivo no aparece acá: va solo a la cuenta de efectivo. Los cheques conviene asignarlos recién cuando se depositan.
      </p>
    </div>
  );
}
